import type { JSX } from 'preact';
import { useEffect, useId, useRef, useState } from 'preact/hooks';
import { DIAS_SEMANA, MESES, foraDoLimite, formatarBr, gradeDoMes, hojeIso, lerBr, lerIso, mascaraBr, porExtenso, somarDias, somarMeses } from '../calendario';
import { Icone } from '../icones';

// ============================================================ senha com "olhinho"

type SenhaProps = {
  id: string;
  value: string;
  /** Sem isto o campo é só para leitura (ex.: senha temporária gerada pelo sistema). */
  onInput?: (valor: string) => void;
  autocomplete?: 'current-password' | 'new-password' | 'off';
  required?: boolean;
  minLength?: number;
  autofocus?: boolean;
  testid?: string;
};

/** Campo de senha com botão para mostrar/ocultar. Começa sempre oculto. */
export function CampoSenha({ id, value, onInput, autocomplete = 'off', required, minLength, autofocus, testid }: SenhaProps) {
  const [ver, setVer] = useState(false);
  return (
    <div class="senha-campo">
      <input id={id} type={ver ? 'text' : 'password'} autocomplete={autocomplete} required={required} minLength={minLength} autofocus={autofocus} readOnly={!onInput} data-testid={testid} value={value} onInput={(e) => onInput?.(e.currentTarget.value)} autocapitalize="off" spellcheck={false} />
      <button
        type="button"
        class="olho"
        aria-label={ver ? 'Ocultar senha' : 'Mostrar senha'}
        aria-pressed={ver}
        title={ver ? 'Ocultar senha' : 'Mostrar senha'}
        // não tira o foco do campo ao clicar (quem está digitando continua digitando)
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => setVer(!ver)}
      >
        <Icone nome={ver ? 'olhoRiscado' : 'olho'} />
      </button>
    </div>
  );
}

// ============================================================ data com calendário próprio

type DataProps = {
  id?: string;
  /** Data em ISO ("2026-11-04") ou "" se vazia. */
  value: string;
  onChange: (iso: string) => void;
  required?: boolean;
  disabled?: boolean;
  min?: string;
  max?: string;
  'aria-label'?: string;
};

const ALTURA_CALENDARIO = 372;

/**
 * Substitui o <input type="date"> do navegador (que ignora o tema e muda de aparência em cada aparelho).
 * Dá para digitar (dd/mm/aaaa, as barras entram sozinhas) ou abrir o calendário. O valor continua em ISO, como antes.
 */
export function CampoData({ id, value, onChange, required, disabled, min, max, 'aria-label': ariaLabel }: DataProps) {
  const [texto, setTexto] = useState(formatarBr(value));
  const [aberto, setAberto] = useState(false);
  const raiz = useRef<HTMLDivElement>(null);
  const entrada = useRef<HTMLInputElement>(null);
  const botao = useRef<HTMLButtonElement>(null);

  // O valor mudou por fora (ex.: formulário reaberto, limpou): reflete no texto, sem atropelar quem está digitando.
  useEffect(() => {
    if (value !== (lerBr(texto) ?? '')) setTexto(formatarBr(value));
  }, [value]);

  // Mensagem de validação do navegador (o "required" e o aviso de data inválida aparecem como nos outros campos).
  useEffect(() => {
    const el = entrada.current;
    if (!el) return;
    const iso = lerBr(texto);
    if (texto !== '' && iso === null) el.setCustomValidity('Digite uma data válida no formato dd/mm/aaaa.');
    else if (iso && foraDoLimite(iso, min, max)) el.setCustomValidity(`Escolha uma data ${min && iso < min ? `a partir de ${formatarBr(min)}` : `até ${formatarBr(max)}`}.`);
    else el.setCustomValidity('');
  }, [texto, min, max]);

  function digitou(bruto: string) {
    // Colou uma data em ISO (2026-01-31), como vem de planilhas e da API: aceita e mostra como dd/mm/aaaa.
    const colouIso = /^\d{4}-\d{2}-\d{2}$/.test(bruto) && lerIso(bruto) ? bruto : null;
    const t = colouIso ? formatarBr(colouIso) : mascaraBr(bruto);
    setTexto(t);
    const iso = lerBr(t);
    const novo = iso && !foraDoLimite(iso, min, max) ? iso : '';
    if (novo !== value) onChange(novo);
  }

  function escolher(iso: string) {
    setTexto(formatarBr(iso));
    onChange(iso);
    setAberto(false);
    botao.current?.focus();
  }

  return (
    <div
      class="data-campo"
      ref={raiz}
      // Saiu o foco de dentro do campo+calendário: fecha o calendário.
      // (relatedTarget vazio = clique num botão no Safari, que não dá foco a botões: não conta como sair.)
      onFocusOut={(e) => {
        const para = e.relatedTarget as Node | null;
        if (aberto && para && !raiz.current?.contains(para)) setAberto(false);
      }}
    >
      <input
        id={id}
        ref={entrada}
        type="text"
        inputMode="numeric"
        placeholder="dd/mm/aaaa"
        maxLength={10}
        autocomplete="off"
        required={required}
        disabled={disabled}
        aria-label={ariaLabel}
        value={texto}
        onInput={(e) => digitou(e.currentTarget.value)}
        onFocus={() => setAberto(false)}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown' && e.altKey) {
            e.preventDefault();
            setAberto(true);
          }
        }}
      />
      <button type="button" class="data-botao" ref={botao} disabled={disabled} aria-label="Abrir calendário" aria-haspopup="dialog" aria-expanded={aberto} onClick={() => setAberto(!aberto)}>
        <Icone nome="calendario" />
      </button>
      {aberto && <Calendario valor={value} min={min} max={max} podeLimpar={!required} ancora={raiz} onEscolher={escolher} onFechar={() => { setAberto(false); botao.current?.focus(); }} />}
    </div>
  );
}

type CalProps = {
  valor: string;
  min?: string;
  max?: string;
  podeLimpar: boolean;
  ancora: { current: HTMLElement | null };
  onEscolher: (iso: string) => void;
  onFechar: () => void;
};

function Calendario({ valor, min, max, podeLimpar, ancora, onEscolher, onFechar }: CalProps) {
  const hoje = hojeIso();
  const inicial = lerIso(valor) ? valor : hoje;
  const [foco, setFoco] = useState(inicial); // dia com o cursor do teclado
  // Posição: logo abaixo do campo; se não couber, acima. No celular o CSS vira uma folha no pé da tela (ignora isto).
  // Calculada já na primeira renderização: um calendário invisível (visibility:hidden) não aceita foco, e o foco no dia é pedido ao abrir.
  const calcularPosicao = (): JSX.CSSProperties => {
    const r = ancora.current?.getBoundingClientRect();
    if (!r) return {};
    const largura = Math.min(320, window.innerWidth - 16);
    const esquerda = Math.max(8, Math.min(r.left, window.innerWidth - largura - 8));
    const cabeEmbaixo = r.bottom + 6 + ALTURA_CALENDARIO <= window.innerHeight;
    return { left: `${esquerda}px`, width: `${largura}px`, top: cabeEmbaixo ? `${r.bottom + 6}px` : `${Math.max(8, r.top - 6 - ALTURA_CALENDARIO)}px` };
  };
  const [pos, setPos] = useState<JSX.CSSProperties>(calcularPosicao);
  const ref = useRef<HTMLDivElement>(null);
  const titulo = useId();
  const x = lerIso(foco)!;
  const grade = gradeDoMes(x.a, x.m);

  // Acompanha rolagem e redimensionamento enquanto aberto.
  useEffect(() => {
    const reposicionar = () => setPos(calcularPosicao());
    window.addEventListener('resize', reposicionar);
    window.addEventListener('scroll', reposicionar, true);
    return () => {
      window.removeEventListener('resize', reposicionar);
      window.removeEventListener('scroll', reposicionar, true);
    };
  }, []);

  // O foco vai para o dia escolhido ao abrir e acompanha as setas do teclado (mas não "foge" dos botões de mês/ano ao clicar neles).
  const moverFoco = useRef(true);
  useEffect(() => {
    if (!moverFoco.current) return;
    moverFoco.current = false;
    ref.current?.querySelector<HTMLButtonElement>('button[data-foco="1"]')?.focus();
  }, [foco]);

  // Esc e clique fora fecham. O Esc é ouvido no documento (e não só dentro do calendário) porque o foco pode estar no campo de
  // texto; o Modal ignora o Esc enquanto existir um .cal-pop, para fechar só o calendário e não a janela inteira.
  useEffect(() => {
    const fora = (e: PointerEvent) => {
      const alvo = e.target as Node;
      if (!ref.current?.contains(alvo) && !ancora.current?.contains(alvo)) onFechar();
    };
    const esc = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      e.stopPropagation();
      onFechar();
    };
    document.addEventListener('pointerdown', fora, true);
    document.addEventListener('keydown', esc, true);
    return () => {
      document.removeEventListener('pointerdown', fora, true);
      document.removeEventListener('keydown', esc, true);
    };
  }, []);

  function aoTeclar(e: KeyboardEvent) {
    const mover = (iso: string) => {
      e.preventDefault();
      moverFoco.current = true;
      setFoco(iso);
    };
    switch (e.key) {
      case 'ArrowLeft': return mover(somarDias(foco, -1));
      case 'ArrowRight': return mover(somarDias(foco, 1));
      case 'ArrowUp': return mover(somarDias(foco, -7));
      case 'ArrowDown': return mover(somarDias(foco, 7));
      case 'PageUp': return mover(somarMeses(foco, e.shiftKey ? -12 : -1));
      case 'PageDown': return mover(somarMeses(foco, e.shiftKey ? 12 : 1));
      case 'Home': return mover(somarDias(foco, -new Date(Date.UTC(x.a, x.m - 1, x.d)).getUTCDay()));
      case 'End': return mover(somarDias(foco, 6 - new Date(Date.UTC(x.a, x.m - 1, x.d)).getUTCDay()));
    }
  }

  const irMes = (n: number) => setFoco(somarMeses(foco, n));

  return (
    <>
      <div class="cal-fundo" aria-hidden="true" />
      <div class="cal-pop" ref={ref} role="dialog" aria-modal="false" aria-labelledby={titulo} style={pos} onKeyDown={aoTeclar}>
        <div class="cal-topo">
          <button type="button" class="cal-nav" aria-label="Ano anterior" onClick={() => irMes(-12)}>«</button>
          <button type="button" class="cal-nav" aria-label="Mês anterior" onClick={() => irMes(-1)}><Icone nome="setaEsq" tamanho={16} /></button>
          <strong id={titulo} class="cal-titulo" aria-live="polite">{MESES[x.m - 1]} de {x.a}</strong>
          <button type="button" class="cal-nav" aria-label="Próximo mês" onClick={() => irMes(1)}><Icone nome="setaDir" tamanho={16} /></button>
          <button type="button" class="cal-nav" aria-label="Próximo ano" onClick={() => irMes(12)}>»</button>
        </div>
        <table class="cal-grade" role="grid" aria-labelledby={titulo}>
          <thead>
            <tr>
              {DIAS_SEMANA.map((d) => (
                <th key={d} scope="col" abbr={d}>{d}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {[0, 1, 2, 3, 4, 5].map((s) => (
              <tr key={s}>
                {grade.slice(s * 7, s * 7 + 7).map((c) => {
                  const bloqueado = foraDoLimite(c.iso, min, max);
                  const sel = c.iso === valor;
                  const classe = ['cal-dia', c.doMes ? '' : 'fora', sel ? 'sel' : '', c.iso === hoje ? 'hoje' : ''].filter(Boolean).join(' ');
                  return (
                    <td key={c.iso} role="gridcell" aria-selected={sel}>
                      <button type="button" class={classe} data-foco={c.iso === foco ? '1' : undefined} tabIndex={c.iso === foco ? 0 : -1} disabled={bloqueado} aria-label={porExtenso(c.iso)} aria-current={c.iso === hoje ? 'date' : undefined} onClick={() => onEscolher(c.iso)}>
                        {c.dia}
                      </button>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
        <div class="cal-rodape">
          <button type="button" class="link" disabled={foraDoLimite(hoje, min, max)} onClick={() => onEscolher(hoje)}>Hoje</button>
          {podeLimpar && valor && <button type="button" class="link" onClick={() => onEscolher('')}>Limpar</button>}
          <button type="button" class="link cal-fechar-celular" onClick={onFechar}>Fechar</button>
        </div>
      </div>
    </>
  );
}
